/**
 * MatchPreviewCards — three anonymised match cards shown on the homepage
 * hero (replacing the animated LiveMatchPreview mock). Fetches from
 * /api/matches/preview when the endpoint exists; falls back to clearly-
 * labelled demo data so the section never renders blank.
 *
 * Card variants:
 *   ★ BEST MATCH   — highest overall fit score
 *   💰 BEST VALUE   — best score-per-dollar
 *   🎯 STRETCH      — a candidate slightly outside the buyer's budget/tow
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Star, CircleDollarSign, Target, AlertTriangle, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEMO_CARDS = [
  {
    variant: 'best_match',
    rv_name: '2024 Grand Design Reflection 31MB',
    match_score: 94,
    best_for: 'Full-time couples with a home office',
    why: '30-ft with a slide, dry weight 8,000 lb fits a 3/4-ton pickup, huge rear kitchen.',
    tradeoff: 'No bunkhouse — not ideal if kids camp too.',
  },
  {
    variant: 'best_value',
    rv_name: '2023 Rockwood Ultra Lite 2109S',
    match_score: 87,
    best_for: 'Weekenders who want low tow weight',
    why: '$22K under your ceiling, 5-year cost-of-ownership 30% lower than the class average.',
    tradeoff: 'Cabinets a bit shorter than premium brands.',
  },
  {
    variant: 'stretch',
    rv_name: '2025 Airstream Flying Cloud 27FB',
    match_score: 78,
    best_for: 'Buyers who value resale and quality',
    why: 'Legendary build + resale — could actually cost less over 10 years despite the sticker.',
    tradeoff: '$12K over your stated budget.',
  },
];

const VARIANTS = {
  best_match: {
    label: 'BEST MATCH',
    Icon: Star,
    ring: 'ring-amber-400/50',
    accentText: 'text-amber-300',
    glow: 'from-amber-400/25 to-transparent',
  },
  best_value: {
    label: 'BEST VALUE',
    Icon: CircleDollarSign,
    ring: 'ring-emerald-400/50',
    accentText: 'text-emerald-300',
    glow: 'from-emerald-400/25 to-transparent',
  },
  stretch: {
    label: 'STRETCH OPTION',
    Icon: Target,
    ring: 'ring-cyan-400/50',
    accentText: 'text-cyan-300',
    glow: 'from-cyan-400/25 to-transparent',
  },
};

const MatchPreviewCards = ({ isDemo = false }) => {
  const [cards, setCards] = useState([]);
  const [demoLabel, setDemoLabel] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/matches/preview`);
        if (cancelled) return;
        const rows = res?.data?.cards || [];
        if (rows.length >= 3) {
          setCards(rows.slice(0, 3));
          setDemoLabel(!!res.data.is_demo);
          return;
        }
      } catch { /* fall through to demo */ }
      if (!cancelled) {
        setCards(DEMO_CARDS);
        setDemoLabel(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="relative max-w-6xl mx-auto" data-testid="match-preview-cards">
      {(isDemo || demoLabel) && (
        <div className="text-center mb-4">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[10px] font-bold tracking-widest text-slate-300">
            <Sparkles className="w-3 h-3" /> SAMPLE MATCHES — YOUR RESULTS WILL BE PERSONALIZED
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-5">
        {cards.map((c, idx) => {
          const v = VARIANTS[c.variant] || VARIANTS.best_match;
          const Icon = v.Icon;
          return (
            <div
              key={idx}
              data-testid={`match-card-${c.variant}`}
              className={`relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-5 sm:p-6 ring-1 ${v.ring} overflow-hidden`}
            >
              <div className={`pointer-events-none absolute -top-16 -right-16 w-48 h-48 rounded-full blur-3xl bg-gradient-to-br ${v.glow}`} />

              <div className="relative">
                <div className="flex items-center justify-between mb-3">
                  <div className={`inline-flex items-center gap-1.5 text-[10px] font-black tracking-widest ${v.accentText}`}>
                    <Icon className="w-3.5 h-3.5" />
                    {v.label}
                  </div>
                  <div className="text-right">
                    <div className={`text-2xl font-black ${v.accentText}`}>{c.match_score}%</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400">match</div>
                  </div>
                </div>

                <h4 className="text-base sm:text-lg font-black text-white leading-tight mb-3">
                  {c.rv_name}
                </h4>

                <div className="space-y-2.5 text-sm">
                  {c.best_for && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">Best for</div>
                      <div className="text-slate-200">{c.best_for}</div>
                    </div>
                  )}
                  {c.why && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">Why it fits</div>
                      <div className="text-slate-200">{c.why}</div>
                    </div>
                  )}
                  {c.tradeoff && (
                    <div className="flex gap-2 items-start pt-2 border-t border-white/10">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-300" />
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-0.5">Tradeoff</div>
                        <div className="text-slate-300">{c.tradeoff}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MatchPreviewCards;
